import {SortType} from "../../../../utils/sorting";
import {isPropertyOutlier} from "./outlierLogic";
import {MetricKey, PropertyDataHealthRow, Thresholds} from "./types";

export type SortField =
    | "property"
    | `${MetricKey}.base`
    | `${MetricKey}.comparison`
    | `${MetricKey}.absoluteChange`
    | `${MetricKey}.percentChange`;

export type SortState = {field: SortField; direction: SortType} | null;

function extractValue(row: PropertyDataHealthRow, field: SortField): number | string | null {
    if (field === "property") {
        return row.propertyName;
    }
    const [metric, sub] = field.split(".") as [MetricKey, "base" | "comparison" | "absoluteChange" | "percentChange"];
    const value = row[metric]?.[sub];
    return value == null ? null : Number(value);
}


export function sortRows(
    rows: PropertyDataHealthRow[],
    sortState: SortState,
    thresholds: Thresholds,
): PropertyDataHealthRow[] {
    if (!sortState) {
        return [...rows].sort((a, b) => {
            const aOut = isPropertyOutlier(a, thresholds);
            const bOut = isPropertyOutlier(b, thresholds);
            if (aOut !== bOut) return aOut ? -1 : 1;
            return a.propertyName.localeCompare(b.propertyName);
        });
    }

    const {field, direction} = sortState;
    const dir = direction === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
        const aVal = extractValue(a, field);
        const bVal = extractValue(b, field);

        if (aVal == null && bVal == null) return a.propertyName.localeCompare(b.propertyName);
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === "string" && typeof bVal === "string") {
            return aVal.localeCompare(bVal) * dir;
        }
        if (typeof aVal === "number" && typeof bVal === "number") {
            return (aVal - bVal) * dir;
        }
        return 0;
    });
}

export function nextSortState(current: SortState, field: SortField): SortState {
    if (!current || current.field !== field) {
        return {field, direction: "asc"};
    }
    if (current.direction === "asc") {
        return {field, direction: "desc"};
    }
    return null;
}
